/* Accounts — the tab is hidden for an admin, but every route it calls checks the session
 * again, so hiding it only avoids offering a control that would be refused.
 *
 * Two rules stop the site from locking itself out, both enforced server-side: nobody may
 * change their own role, deactivate themselves or delete their own account, and an admin must
 * always be given at least one system.
 */

import { useState } from 'react';
import {
  Button,
  Checkbox,
  ConfirmModal,
  Input,
  Modal,
  SimpleDropdown,
} from '@nextleap-al/admin-ui';
import { Pencil } from 'lucide-react';

import { detailOf, write } from '../api/client';
import type { Operator, UsersResponse } from '../api/types';
import { Async, PanelCard, ResultLine } from '../components/Panel';
import { Signal, Signals } from '../components/Signal';
import { Muted, Table } from '../components/Table';
import { TileRow, type TileSpec } from '../components/Tiles';
import { useApi } from '../hooks/useApi';
import { num, shortDate } from '../lib/format';
import { useApp } from '../state/app';

const ROLES = [
  { value: 'admin', label: 'Admin — one or more systems' },
  { value: 'superadmin', label: 'Superadmin — every system, manages accounts' },
];

/**
 * Whether the username is worth printing next to the display name: it earns its place only
 * when the name does not already contain it. Compared on letters and digits alone, so
 * punctuation does not make two spellings of one thing look different.
 */
function saysSomethingNew(displayName: string | null | undefined, username: string): boolean {
  if (!displayName) return false; // the name shown IS the username; printing it again says nothing
  const plain = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  return !plain(displayName).includes(plain(username));
}

/** Tick-boxes for the systems a role may see. Built from the server's list, so a system
 *  added to the corpus appears here without a code change. */
function SystemChecks({
  systems,
  value,
  onChange,
}: {
  systems: string[];
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { systemLabel } = useApp();

  if (!systems.length) {
    return (
      <p className="text-sm text-[var(--text-muted)]">No systems found in the corpus yet.</p>
    );
  }
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2">
      {systems.map((s) => (
        <Checkbox
          key={s}
          label={systemLabel(s)}
          checked={value.includes(s)}
          onChange={(e) =>
            onChange(e.target.checked ? [...value, s] : value.filter((x) => x !== s))
          }
        />
      ))}
    </div>
  );
}

function CreateForm({ systems, onDone }: { systems: string[]; onDone: () => void }) {
  const { reload } = useApp();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('admin');
  const [granted, setGranted] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    const { ok, data } = await write('/api/users', {
      username: username.trim(),
      display_name: displayName.trim(),
      password,
      role,
      systems: role === 'superadmin' ? '' : granted.join(','),
    });
    setBusy(false);
    if (!ok) {
      setResult({ ok, text: detailOf(data, 'The account could not be created.') });
      return;
    }
    reload();
    onDone();
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-4 rounded-[var(--radius-md)] border border-[var(--border-light)] bg-[var(--bg-elevated)] p-4"
    >
      <div className="grid gap-4 md:grid-cols-3">
        <Input
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="off"
          placeholder="e.g. eris"
          required
        />
        <Input
          label="Full name (optional)"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          autoComplete="off"
          placeholder="e.g. Eris Hoxha"
        />
        <Input
          label="Password"
          hint="At least 10 characters."
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          minLength={10}
          required
        />
      </div>
      <SimpleDropdown
        label="Role"
        value={role}
        onChange={(value) => setRole(String(value))}
        options={ROLES}
      />
      {/* A superadmin sees everything by definition, so the picker is meaningless for that
          role and hiding it is clearer than leaving it there to be ignored. */}
      {role === 'admin' && (
        <div>
          <span className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">
            Systems this admin may see
          </span>
          <SystemChecks systems={systems} value={granted} onChange={setGranted} />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" isLoading={busy}>
          Create account
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {result && <ResultLine ok={result.ok}>{result.text}</ResultLine>}
    </form>
  );
}

/**
 * Everything one account can be changed to, in a dialog. Mounted only while open and keyed on
 * the account, so the fields start from that account's values rather than the last one's.
 */
function UserEditor({
  user,
  systems,
  isSelf,
  onClose,
}: {
  user: Operator;
  systems: string[];
  isSelf: boolean;
  onClose: () => void;
}) {
  const { reload } = useApp();
  const [role, setRole] = useState(user.role);
  const [granted, setGranted] = useState<string[]>(user.systems);
  const [password, setPassword] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  // Only a success redraws: a refusal leaves the dialog open with the message next to the
  // control that was refused, which is where it can be acted on.
  const run = async (promise: ReturnType<typeof write>, done: string) => {
    const { ok, data } = await promise;
    setResult({ ok, text: ok ? done : detailOf(data, 'That did not work.') });
    if (ok) reload();
    return ok;
  };

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={user.display_name || user.username}
        // Same rule as the table row this opens from: the username only when the heading above
        // it does not already say it, so the dialog does not greet you with your name twice.
        description={
          [saysSomethingNew(user.display_name, user.username) ? user.username : '', isSelf ? 'this is you' : '']
            .filter(Boolean)
            .join(' · ')
        }
        size="lg"
      >
        <div className="space-y-4">
          <SimpleDropdown
            label="Role"
            value={role}
            onChange={(value) => setRole(value as Operator['role'])}
            options={[
              { value: 'admin', label: 'Admin — selected systems only' },
              { value: 'superadmin', label: 'Superadmin — every system' },
            ]}
          />
          {role === 'admin' && (
            <div>
              <span className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">
                Systems this admin may see
              </span>
              <SystemChecks systems={systems} value={granted} onChange={setGranted} />
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                run(
                  write(`/api/users/${user.id}`, {
                    role,
                    systems: role === 'superadmin' ? '' : granted.join(','),
                  }),
                  `${user.username} updated.`,
                )
              }
            >
              Save changes
            </Button>
            {!isSelf && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    run(
                      write(`/api/users/${user.id}`, { active: user.active ? 'false' : 'true' }),
                      `${user.username} ${user.active ? 'disabled' : 'enabled'}.`,
                    )
                  }
                >
                  {user.active ? 'Disable account' : 'Enable account'}
                </Button>
                <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
                  Delete account
                </Button>
              </>
            )}
          </div>

          <div className="border-t border-[var(--border-light)] pt-4">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Set a new password</h3>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="min 10 characters"
                aria-label={`New password for ${user.username}`}
                className="w-[240px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!password) {
                    setResult({ ok: false, text: 'Type the new password first.' });
                    return;
                  }
                  void run(
                    write(`/api/users/${user.id}/password`, { password }),
                    `Password changed — ${user.username} has been signed out everywhere.`,
                  );
                  setPassword('');
                }}
              >
                Set password
              </Button>
            </div>
            <p className="mt-2 text-sm text-[var(--text-muted)]">
              Resetting a password signs that account out everywhere.
            </p>
          </div>

          {result && <ResultLine ok={result.ok}>{result.text}</ResultLine>}
        </div>
      </Modal>

      {/* Irreversible, and the button beside it does the thing usually meant — so the
          confirmation names the account and points at the alternative. */}
      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={async () => {
          setConfirmDelete(false);
          // Closed on success: the dialog is headed by an account that no longer exists.
          if (await run(write(`/api/users/${user.id}`, {}, 'DELETE'), '')) onClose();
        }}
        title={`Delete "${user.username}" permanently?`}
        message="Disabling the account instead keeps the record of what it did."
        variant="danger"
        confirmLabel="Delete"
      />
    </>
  );
}

/**
 * Who holds a key to this site. An account that has never signed in is usually a password
 * set and never passed on, and one with no system granted can sign in and see nothing.
 */
function tilesFor(data: UsersResponse): TileSpec[] {
  return [
    { label: 'Accounts', value: num(data.items.length), hint: 'people who can sign in here' },
    {
      label: 'Superadmins',
      value: num(data.items.filter((u) => u.role === 'superadmin').length),
      hint: 'every system, and can manage these accounts',
    },
    {
      label: 'Disabled',
      value: num(data.items.filter((u) => !u.active).length),
      hint: 'kept for the record of what they did, but cannot sign in',
    },
    {
      label: 'Never signed in',
      value: num(data.items.filter((u) => !u.last_login_at).length),
      hint: 'created, but the password has not been used once',
    },
  ];
}

/** What an account may see. A superadmin's reach is the absence of a restriction rather than
 *  a list; an admin with an empty one is a fault, not a setting, so it is coloured as one. */
function Scope({ user }: { user: Operator }) {
  const { systemLabel } = useApp();

  if (user.role === 'superadmin') return <Signal tone="good">every system</Signal>;
  if (!user.systems.length) return <Signal tone="critical">no system granted</Signal>;
  return (
    <Signals>
      {user.systems.map((s) => (
        <Signal key={s}>{systemLabel(s)}</Signal>
      ))}
    </Signals>
  );
}

export function UsersPanel() {
  const query = useApi<UsersResponse>('/api/users');
  const [creating, setCreating] = useState(false);
  // The id, not the row: a save reloads the list, and a dialog holding the object it was
  // opened with would go on offering "Disable account" for one just disabled.
  const [editingId, setEditingId] = useState<number | null>(null);

  return (
    <Async query={query} shape="page">
      {(data) => {
        const editing = data.items.find((u) => u.id === editingId) ?? null;
        return (
        <div className="space-y-5">
          <TileRow tiles={tilesFor(data)} />

          <PanelCard
            title="Accounts"
            hint="A role or system change takes effect on their next request. Disabling or resetting a password ends their sessions at once."
            action={
              !creating && (
                <Button size="sm" onClick={() => setCreating(true)}>
                  Create an account
                </Button>
              )
            }
          >
            <div className="space-y-4">
              {creating && (
                <CreateForm systems={data.systems} onDone={() => setCreating(false)} />
              )}

              <Table
                rows={data.items}
                pageSize={10}
                search="Search by name, username or system"
                empty="No account exists yet, which cannot be true — you are signed in as one."
                columns={[
                  {
                    id: 'account',
                    label: 'Account',
                    render: (u) => (
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-medium text-[var(--text-primary)]">
                            {u.display_name || u.username}
                          </span>
                          {saysSomethingNew(u.display_name, u.username) && <Muted>{u.username}</Muted>}
                          {u.id === data.me && <Muted>· you</Muted>}
                        </div>
                        {!u.active && (
                          <div className="mt-1">
                            <Signal tone="critical">disabled</Signal>
                          </div>
                        )}
                      </div>
                    ),
                    sortValue: (u) => u.display_name || u.username,
                  },
                  {
                    id: 'role',
                    label: 'Role',
                    render: (u) => (
                      <Signal tone={u.role === 'superadmin' ? 'good' : 'plain'}>{u.role}</Signal>
                    ),
                    sortValue: (u) => u.role,
                  },
                  {
                    id: 'systems',
                    label: 'Systems',
                    render: (u) => <Scope user={u} />,
                    // Sorts the two states that need finding — no system granted, then every
                    // system — to the ends, rather than alphabetically by their first grant.
                    sortValue: (u) =>
                      u.role === 'superadmin' ? 'zzz' : u.systems.join(', ') || '',
                  },
                  {
                    id: 'last_login_at',
                    label: 'Last seen',
                    render: (u) =>
                      u.last_login_at ? (
                        shortDate(u.last_login_at)
                      ) : (
                        <Muted>never signed in</Muted>
                      ),
                    // Never-signed-in sorts as absent rather than as the epoch, so it lands
                    // with the other unknowns instead of looking like the oldest login.
                    sortValue: (u) => u.last_login_at ?? null,
                  },
                  {
                    id: 'edit',
                    label: '',
                    render: (u) => (
                      <div className="text-right">
                        {/* Kept a real button, not a decorative icon: the row's own click handler
                            cannot be reached by keyboard, and this is what Tab lands on. The
                            stopPropagation keeps one click from opening the dialog twice. */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(u.id);
                          }}
                          aria-label={`Manage ${u.display_name || u.username}`}
                          title="Manage this account"
                          className="rounded-[var(--radius-sm)] p-1.5 text-[var(--text-muted)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]"
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    ),
                  },
                ]}
                onRowClick={(u) => setEditingId(u.id)}
              />
            </div>
          </PanelCard>

          {editing && (
            <UserEditor
              key={editing.id}
              user={editing}
              systems={data.systems}
              isSelf={editing.id === data.me}
              onClose={() => setEditingId(null)}
            />
          )}
        </div>
        );
      }}
    </Async>
  );
}
